import * as styles from "./ColorPickerSwatchGrid.css";

import { clsx } from "clsx";
import type { JSX, KeyboardEvent } from "react";
import { useRef, useState } from "react";

import { useColorPickerContext } from "./ColorPicker.context";
import type { ColorPickerSwatchGridProps } from "./ColorPicker.types";
import { ColorPickerSwatch } from "./ColorPickerSwatch";
import { normalizeHex } from "./colorMath";
import { SWATCH_SIZE } from "./constants";
import { resolveSwatch } from "./swatch";

export function ColorPickerSwatchGrid({
  colors,
  columns = 8,
  className,
}: ColorPickerSwatchGridProps): JSX.Element {
  const { hex, setHex, size } = useColorPickerContext();
  const gridRef = useRef<HTMLDivElement>(null);
  const swatches = colors.map(resolveSwatch);

  const selectedIndex = swatches.findIndex(
    (swatch) => (normalizeHex(swatch.value) ?? swatch.value) === hex,
  );
  const [activeIndex, setActiveIndex] = useState(Math.max(selectedIndex, 0));

  function focusIndex(index: number) {
    const clamped = Math.min(Math.max(index, 0), swatches.length - 1);

    setActiveIndex(clamped);
    gridRef.current
      ?.querySelectorAll<HTMLButtonElement>("[data-slot='color-picker-swatch']")
      .item(clamped)
      ?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (swatches.length === 0) {
      return;
    }

    switch (event.key) {
      case "ArrowRight":
        focusIndex(activeIndex + 1);
        break;
      case "ArrowLeft":
        focusIndex(activeIndex - 1);
        break;
      case "ArrowDown":
        focusIndex(activeIndex + columns);
        break;
      case "ArrowUp":
        focusIndex(activeIndex - columns);
        break;
      case "Home":
        focusIndex(0);
        break;
      case "End":
        focusIndex(swatches.length - 1);
        break;
      default:
        return;
    }

    event.preventDefault();
  }

  return (
    <div
      ref={gridRef}
      role="radiogroup"
      aria-label="Palette de couleurs"
      data-slot="color-picker-swatch-grid"
      className={clsx(styles.grid, styles.sizeVariants[size], className)}
      style={{
        gridTemplateColumns: `repeat(${columns}, minmax(0, ${SWATCH_SIZE}px))`,
      }}
      onKeyDown={handleKeyDown}
    >
      {swatches.map((swatch, index) => (
        <ColorPickerSwatch
          key={`${swatch.value}-${index}`}
          color={swatch.value}
          label={swatch.label}
          shape="square"
          selected={(normalizeHex(swatch.value) ?? swatch.value) === hex}
          active={index === activeIndex}
          onSelect={() => {
            setActiveIndex(index);
            setHex(swatch.value);
          }}
        />
      ))}
    </div>
  );
}
